import React, { useState, useMemo, useCallback, useEffect } from "react";
import { Tree, Button, Input, Space, Dropdown, Tag, Empty } from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  MoreOutlined,
  SearchOutlined,
  BranchesOutlined,
} from "@ant-design/icons";

const UNASSIGNED = "待分配组织";
const isUnassignedName = (name) => name === UNASSIGNED;

const OrgSidebar = ({
  tree,
  flatList,
  selectedOrgId,
  onSelect,
  onCreate,
  onEdit,
  onMove,
  onDelete,
  canCreate,
  canUpdate,
  canDelete,
  memberCounts = {},
}) => {
  const [search, setSearch] = useState("");
  const [expandedKeys, setExpandedKeys] = useState([]);

  useEffect(() => {
    if (tree?.length > 0) {
      setExpandedKeys(tree.map((n) => String(n.id)));
    }
  }, [tree]);

  const getTotalMemberCount = useCallback((node) => {
    let count = memberCounts[node.id] || 0;
    if (node.children?.length > 0) {
      node.children.forEach((child) => {
        count += getTotalMemberCount(child);
      });
    }
    return count;
  }, [memberCounts]);

  const filterNodes = useCallback((nodes, term) => {
    return nodes.reduce((acc, node) => {
      const match =
        (node.name || "").toLowerCase().includes(term) ||
        (node.description || "").toLowerCase().includes(term);
      const children = node.children ? filterNodes(node.children, term) : [];
      if (match || children.length > 0) {
        acc.push({
          ...node,
          children: children.length > 0 ? children : undefined,
        });
      }
      return acc;
    }, []);
  }, []);

  const filteredTree = useMemo(() => {
    if (!search.trim()) return tree;
    return filterNodes(tree, search.toLowerCase());
  }, [tree, search, filterNodes]);

  const buildTreeData = useCallback(
    (nodes) => {
      if (!Array.isArray(nodes)) return [];
      return nodes.map((node) => {
        const unassigned = isUnassignedName(node.name);
        const items = [
          canCreate && {
            key: "create",
            label: "创建子组织",
            onClick: () => onCreate(node),
          },
          canUpdate && {
            key: "edit",
            label: "编辑",
            icon: <EditOutlined />,
            onClick: () => onEdit(node),
          },
          canUpdate && {
            key: "move",
            label: "移动",
            onClick: () => onMove(node),
          },
          canDelete &&
            !unassigned && {
              key: "delete",
              label: "删除",
              danger: true,
              icon: <DeleteOutlined />,
              onClick: () => onDelete(node.id),
            },
        ].filter(Boolean);

        return {
          key: String(node.id),
          title: (
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                width: "100%",
              }}
            >
              <span
                style={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  flex: 1,
                }}
              >
                <BranchesOutlined style={{ marginRight: 6, opacity: 0.7 }} />
                {node.name}
                {unassigned && (
                  <Tag color="orange" style={{ marginLeft: 6, fontSize: 10 }}>
                    兜底
                  </Tag>
                )}
                <span style={{ color: "#999", fontSize: 11, marginLeft: 4 }}>
                  ({getTotalMemberCount(node)}人)
                </span>
              </span>
              {canUpdate && items.length > 0 && (
                <Dropdown menu={{ items }} trigger={["click"]}>
                  <Button
                    type="text"
                    size="small"
                    icon={<MoreOutlined />}
                    onClick={(e) => e.stopPropagation()}
                  />
                </Dropdown>
              )}
            </div>
          ),
          children:
            node.children?.length > 0
              ? buildTreeData(node.children)
              : undefined,
        };
      });
    },
    [canCreate, canUpdate, canDelete, onCreate, onEdit, onMove, onDelete]
  );

  const treeData = useMemo(
    () => buildTreeData(filteredTree),
    [filteredTree, buildTreeData]
  );

  const selectedOrg = flatList.find((o) => o.id === selectedOrgId);

  return (
    <div
      style={{
        padding: 12,
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div style={{ marginBottom: 12 }}>
        <Space direction="vertical" style={{ width: "100%" }}>
          <Input
            prefix={<SearchOutlined />}
            placeholder="搜索组织"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            allowClear
            size="small"
          />
          {canCreate && (
            <Button
              type="primary"
              size="small"
              block
              icon={<PlusOutlined />}
              onClick={() => onCreate()}
            >
              新建组织
            </Button>
          )}
        </Space>
      </div>
      <div style={{ flex: 1, overflow: "auto" }}>
        {treeData.length > 0 ? (
          <Tree
            treeData={treeData}
            selectedKeys={selectedOrgId ? [String(selectedOrgId)] : []}
            expandedKeys={expandedKeys}
            onExpand={setExpandedKeys}
            onSelect={(keys) =>
              keys.length > 0 && onSelect(Number(keys[0]))
            }
            showLine={{ showLeafIcon: false }}
            blockNode
          />
        ) : (
          <Empty
            description="无组织数据"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        )}
      </div>
      {selectedOrg && (
        <div
          style={{
            paddingTop: 12,
            borderTop: "1px solid #f0f0f0",
            marginTop: 8,
          }}
        >
          <div style={{ fontSize: 12, color: "#666", marginBottom: 4 }}>
            当前选中
          </div>
          <div style={{ fontWeight: 600, fontSize: 14 }}>
            {selectedOrg.name}
          </div>
          <div style={{ fontSize: 12, color: "#999", marginTop: 2 }}>
            {selectedOrg.pathLabel || selectedOrg.name}
          </div>
        </div>
      )}
    </div>
  );
};

export default OrgSidebar;
